/**
 * dateStep — pure "step a native date/time input's value up or down by one unit",
 * for the Alt+↑ / Alt+↓ keyboard gesture on a focused date control (mouse-free,
 * ADR-0013, user request 2026-09-21: plain segment arrows aren't reliable, so
 * Alt+↑/↓ changes the value directly).
 *
 * Works on the input's OWN value string (never a Date instance crossing a
 * timezone), so it round-trips exactly what the native control shows/binds:
 *   · date            → "yyyy-MM-dd"        step = 1 day
 *   · datetime-local  → "yyyy-MM-ddTHH:mm"  step = 1 day (time preserved)
 *   · time            → "HH:mm"             step = 1 minute (wraps 00:00↔23:59)
 *   · month           → "yyyy-MM"           step = 1 month
 *   · week / unknown  → null (caller leaves the native control alone)
 * An empty value seeds from "now" so the first Alt+↑/↓ produces a usable value.
 */
const pad = (n: number, w = 2): string => String(n).padStart(w, '0');

/** Step direction: +1 for Alt+↑ (up), −1 for Alt+↓ (down). */
export type StepDir = 1 | -1;

export function stepDateInputValue(type: string, value: string, dir: StepDir): string | null {
  const t = (type || '').toLowerCase();
  const now = new Date();

  if (t === 'time') {
    const [hhRaw, mmRaw] = (value || '').split(':');
    const hh = Number(hhRaw), mm = Number(mmRaw);
    const base = (Number.isFinite(hh) ? hh : now.getHours()) * 60 + (Number.isFinite(mm) ? mm : now.getMinutes());
    const mins = ((base + dir) % 1440 + 1440) % 1440;   // wrap within the day
    return `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
  }

  if (t === 'month') {
    const [yRaw, mRaw] = (value || '').split('-');
    const y = Number(yRaw) || now.getFullYear();
    const m = Number(mRaw) || (now.getMonth() + 1);
    const d = new Date(y, m - 1 + dir, 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  }

  if (t === 'date' || t === 'datetime-local') {
    let datePart = value;
    let timePart = '';
    if (t === 'datetime-local') {
      const [dp, tp] = (value || '').split('T');
      datePart = dp || '';
      timePart = tp || `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    }
    const [yRaw, mRaw, dRaw] = (datePart || '').split('-');
    const y = Number(yRaw) || now.getFullYear();
    const m = Number(mRaw) || (now.getMonth() + 1);
    const d = Number(dRaw) || now.getDate();
    const dt = new Date(y, m - 1, d + dir);            // add/subtract one calendar day
    const out = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    return t === 'datetime-local' ? `${out}T${timePart}` : out;
  }

  return null;   // week / unknown → leave the native control to handle it
}
